/**
 * Demo Mode Configuration
 *
 * This file contains all configuration for demo/sales presentation mode.
 * Demo mode is used for events like MarTech Expo 2026 where real customer
 * data should not be displayed, but prospects can still connect their own
 * platform accounts to see live data alongside demo data.
 */

/**
 * Main demo mode flag. Set via environment variable VITE_DEMO_MODE=true
 * Works in both Vite (browser) and Node.js environments
 */
const getDemoMode = (): boolean => {
  // Vite environment (browser)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env.VITE_DEMO_MODE === 'true';
  }
  // Node.js environment
  if (typeof process !== 'undefined' && process.env) {
    return process.env.VITE_DEMO_MODE === 'true';
  }
  return false;
};

export const DEMO_MODE = getDemoMode();

/**
 * Demo workspace ID for complete isolation from real customer data.
 * All demo data lives in this dedicated workspace.
 * NOTE: Must be a valid UUID for database compatibility
 */
export const DEMO_WORKSPACE_ID = '01234567-89ab-cdef-0123-456789abcdef';

/**
 * Demo user credentials for easy access during sales presentations.
 * This single account has access to all demo workspaces for easy switching.
 * These are clearly marked as DEMO-ONLY in the UI.
 */
export const DEMO_CREDENTIALS = {
  email: 'demo@ydm-media-intelligence.com',
  password: 'MarTech2026Demo!',
  displayName: 'Demo User (All Workspaces)',
};

/**
 * Demo workspace configuration
 */
export const DEMO_WORKSPACE = {
  id: DEMO_WORKSPACE_ID,
  name: 'YDM Demo Workspace',
  slug: 'martech-expo-2026-demo',
  description: 'Sales demonstration workspace for MarTech Expo 2026',
};

/**
 * Platform connection demo status
 * Used to distinguish between demo data and live connections
 */
export enum DemoConnectionStatus {
  DEMO_DATA = 'demo_data',
  LIVE_CONNECTION = 'live_connection',
}

/**
 * Demo-specific feature flags
 */
export const DEMO_FEATURES = {
  // Allow users to connect their real platform accounts in demo mode
  allowLiveConnections: true,

  // Show "DEMO MODE" badge in header
  showDemoBadge: true,

  // Show demo data warning banner
  showWarningBanner: true,

  // Pre-fill demo credentials on signin page
  autoFillCredentials: true,
} as const;

/**
 * Demo data time range
 */
export const DEMO_DATE_RANGE = {
  // Generate 90 days of historical data
  days: 90,

  // Start date (90 days ago from today)
  get startDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() - this.days);
    return date;
  },

  // End date (today)
  get endDate(): Date {
    return new Date();
  },
};

/**
 * Demo performance configuration
 * Controls the realism and variation in demo campaign performance
 */
export const DEMO_PERFORMANCE_CONFIG = {
  // Enable realistic performance tiers (varied ROAS, CTR across campaigns)
  enableRealisticPerformance: true,

  // Include underperforming campaigns in demo data
  includeUnderperformers: true,

  // Enable variance in daily metrics (makes trends look more natural)
  varianceEnabled: true,

  // Performance tier distribution (must sum to 1.0)
  tierDistribution: {
    tier1: 0.3,  // 30% high performers (ROAS 3.5-5x)
    tier2: 0.4,  // 40% good performers (ROAS 2-3.5x)
    tier3: 0.2,  // 20% average performers (ROAS 1-2x)
    tier4: 0.1,  // 10% underperformers (ROAS 0.5-1x)
  },
} as const;
